import React from 'react';
import { Box, Typography, Stack, Chip, useTheme, Divider } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { InfoTooltip } from './InfoTooltip';

interface MetricCardData {
    label: string;
    value: string | number;
    unit?: string;
    metricValueColor?: string;
    trend?: string;
    color?: string;
    icon?: React.ElementType;
    tooltipExplanation?: string;
}

export interface MetricCardsWidgetProps {
    title?: string;
    metrics: MetricCardData[];
    orientation?: 'horizontal' | 'vertical';
}

const MetricCardsWidget: React.FC<MetricCardsWidgetProps> = ({ title, metrics, orientation = 'horizontal' }) => {
    const theme = useTheme();
    const getTrendIcon = (trend?: string) => {
        if (!trend) return undefined;
        const trendValue = parseFloat(trend);
        if (trendValue > 0) {
            return <ArrowUpwardIcon fontSize="small" sx={{ color: theme.palette.boxColor.greenTrend.color }} />;
        } else if (trendValue < 0) {
            return <ArrowDownwardIcon fontSize="small" sx={{ color: theme.palette.boxColor.redTrend.color }} />;
        }
        return undefined;
    };

    const getChipColor = (color?: string): 'success' | 'error' | 'info' => {
        if (color === 'green') return 'success';
        if (color === 'red') return 'error';
        return 'info';
    };

    return (
        <Box
            sx={{
                backgroundColor: theme.palette.backgroundLight.primary,
                color: theme.palette.widget.text.main,
                pb: '16px',
                borderRadius: '4px',
                display: 'flex',
                flexDirection: 'column',
                flexWrap: 'wrap',
                justifyContent: 'space-around',
                width: '100%',
                minWidth: 0,
            }}
        >
            {title && (
                <Typography variant="tabPageHeader" sx={{ width: '100%', marginBottom: '10px' }}>
                    {title}
                </Typography>
            )}

            <Stack
                direction="row"
                sx={{
                    overflowX: 'hidden',
                    backgroundColor: theme.palette.backgroundLight.secondary,
                    flexWrap: 'wrap',
                    width: '100%',
                    minWidth: 0,
                }}
            >
                {metrics.map((metric, index) => {
                    const chipColor = getChipColor(metric.color);
                    return (
                        <React.Fragment key={`${metric.label}-${metric.value}-${index}`}>
                            <Box
                                sx={{
                                    backgroundColor: theme.palette.backgroundLight.secondary,
                                    color: theme.palette.widget.text.value,
                                    padding: '12px',
                                    borderRadius: '2px',
                                    flex:
                                        orientation === 'horizontal'
                                            ? { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 180px' }
                                            : '1 1 100%',
                                    minWidth: 0,
                                    maxWidth: orientation === 'horizontal' ? { xs: '100%', sm: '100%', md: '350px' } : '100%',
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 1,
                                }}
                            >
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                                    {metric.icon && (
                                        <metric.icon
                                            sx={{
                                                fontSize: 20,
                                                color: theme.palette.text.primary,
                                                flexShrink: 0,
                                            }}
                                        />
                                    )}

                                    <Typography
                                        variant="subtitle2"
                                        title={metric.label}
                                        sx={{
                                            color: theme.palette.text.primary,
                                            fontWeight: 500,
                                            lineHeight: 1.25,
                                            minWidth: 0,
                                            maxWidth: '100%',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {metric.label}
                                    </Typography>
                                    {metric.tooltipExplanation && (
                                        <InfoTooltip
                                            label={[
                                                {
                                                title: metric.label,
                                                description: metric.tooltipExplanation,
                                                },
                                            ]}
                                        />
                                    )}
                                </Stack>

                                <Typography
                                    variant="subtitle2"
                                    sx={{
                                        color: metric.metricValueColor ?? theme.palette.text.primary,
                                        ml: 1,
                                        fontWeight: 500,
                                        whiteSpace: 'normal',
                                        overflowWrap: 'anywhere',
                                        wordBreak: 'break-word',
                                        lineHeight: 1.25,
                                        textAlign: 'right',
                                        flexShrink: 0,
                                    }}
                                >
                                    {metric.value} {metric.unit}
                                </Typography>
                                {metric.trend && (
                                    <Chip
                                        label={metric.trend}
                                        icon={getTrendIcon(metric.trend)}
                                        color={chipColor}
                                        sx={{
                                            backgroundColor: `${theme.palette.boxColor[metric?.color + 'Trend']?.bgColor || 'gray'} !important`,
                                            color: `${theme?.palette?.boxColor[metric?.color + 'Trend' || '']?.color || 'black'} !important`,
                                            fontWeight: 'semiBold',
                                            fontSize: '16px !important',
                                            height: '24px !important',
                                            borderRadius: '5px',
                                            '& .MuiChip-icon': {
                                                color: theme?.palette?.boxColor[metric?.color + 'Trend' || '']?.color || 'black',
                                            },
                                            '& .MuiChip-label': {
                                                fontWeight: '500',
                                                fontSize: '16px!important',
                                            },
                                            '& .MuiChip-colorSuccess': {
                                                // backgroundColor: `black !important`,
                                                // color: theme?.palette?.boxColor[metric?.color || '']?.color,
                                                mr: 0,
                                                mb: 0,
                                            },
                                            '& .MuiChip-colorError': {
                                                // backgroundColor: `${theme.palette.error.light}11 !important`,
                                                // color: theme?.palette?.boxColor[metric?.color || '']?.color,
                                                mr: 0,
                                                mb: 0,
                                            },
                                            ml: 1,

                                        }}
                                        size="small"
                                    />
                                )}
                            </Box>

                            {index < metrics.length - 1 && (
                                <Divider
                                    orientation="vertical"
                                    variant="middle"
                                    flexItem
                                    sx={{
                                        color: theme.palette.buttons.primary.color,
                                        bgcolor: theme.palette.buttons.primary.color,
                                        width: '1.5px',
                                        display: { xs: 'none', md: 'block' },
                                    }}
                                />
                            )}
                        </React.Fragment>
                    );
                })}
            </Stack>
            
        </Box>
    );
};

export default MetricCardsWidget;